import { Tooltip, type TooltipProps } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

type OverflowTooltipProps = {
  title: React.ReactNode;
  children: React.ReactElement<any>;
} & Omit<TooltipProps, 'title' | 'children'>;

type ChildWithOptionalRef = React.ReactElement<any> & {
  ref?: React.Ref<HTMLElement>;
};

export const OverflowTooltip = ({ title, children, ...tooltipProps }: OverflowTooltipProps) => {
  const [isOverflow, setIsOverflow] = useState<boolean>(false);
  const ref = useRef<HTMLElement>(null);
  const childWithRef = children as ChildWithOptionalRef;

  const checkOverflow = () => {
    const element = ref.current;
    if (!element) return false;

    const isContentOverflowing =
      element.clientWidth < element.scrollWidth || element.clientHeight < element.scrollHeight;

    if (isContentOverflowing !== isOverflow) setIsOverflow(isContentOverflowing);
    return isContentOverflowing;
  };

  useEffect(() => {
    const measureOverflow = () => {
      checkOverflow();
    };

    const frameId = requestAnimationFrame(measureOverflow);

    if (typeof ResizeObserver === 'undefined' || !ref.current) {
      return () => cancelAnimationFrame(frameId);
    }

    const observer = new ResizeObserver(measureOverflow);
    observer.observe(ref.current);

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  });

  const contentElement = React.cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      ref.current = node;
      if (typeof childWithRef.ref === 'function') {
        childWithRef.ref(node);
      } else if (childWithRef.ref) {
        childWithRef.ref.current = node;
      }
    },
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      checkOverflow();
      children.props.onMouseEnter?.(event);
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      checkOverflow();
      children.props.onFocus?.(event);
    },
  });

  return isOverflow ? (
    <Tooltip title={title} {...tooltipProps}>
      {contentElement}
    </Tooltip>
  ) : (
    contentElement
  );
};
